/** RN08 / RN-1, RN-14, RN-17: the hosted fleet's closed browser projection.
 * RN10 binds the RN07 routes. GET accepts the fleet array or { nodes, viewer_role };
 * an absent/unknown viewer_role is read-only, never inferred from node ownership.
 * RN07 rechecks live membership and entitlement on every control request.
 */
import { ApiClientError, requestJson, responseArray, responseRecord } from './http';

export { ApiClientError } from './http';

export type RenderNodeState = 'online' | 'busy' | 'draining' | 'asleep_wakeable' | 'offline' | 'quarantined';
export type ServiceMode = 'unattended' | 'attended' | 'off';
export type ProcessingMode = 'off' | 'headless_gpu_box' | 'laptop_light';
export type PowerAction = 'sleep' | 'shutdown' | 'restart';
export type WatchdogState = 'ok' | 'backoff' | 'quarantined';
export interface RenderNodePower {
  limit_w: number | null;
  default_limit_w: number | null;
  draw_w: number | null;
  temp_c: number | null;
  cap_required: boolean | null;
  cap_satisfied: boolean | null;
}
export interface RenderNode {
  device_id: string;
  label: string;
  state: RenderNodeState;
  vram_total_mib: number | null;
  vram_free_mib: number | null;
  queue_depth: number | null;
  in_flight: number | null;
  processing_mode: ProcessingMode | null;
  display_class: 'headless_capable' | 'interactive' | null;
  service_mode: ServiceMode | null;
  power: RenderNodePower;
  watchdog: Record<string, WatchdogState>;
  /** Routing ids only; the page never displays waker identities. */
  wake_via: string[];
}
export interface RenderNodeFleet {
  nodes: RenderNode[];
  /** Server membership metadata, not the deployment's owner_org flag. */
  viewer_role: string | null;
}
export interface RenderNodeRequest { idempotencyKey: string }

const root = '/v1/render-nodes';
const id = encodeURIComponent;

function malformed(): never {
  throw new ApiClientError('malformed', 200, 'malformed_response', 'Render node status could not be read.');
}
function identifier(value: unknown): string {
  return typeof value === 'string' && /^[A-Za-z0-9_-]{1,100}$/.test(value) ? value : malformed();
}
function measurement(value: unknown, integer = false): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
    && (!integer || Number.isInteger(value)) ? value : null;
}
function boolean(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null;
}
function oneOf<T extends string>(value: unknown, allowed: readonly T[]): T | null {
  return typeof value === 'string' && allowed.includes(value as T) ? value as T : null;
}

/** RN-18 / BW-24: display-only; labels are never used for identity or dispatch. */
export function safeRenderNodeLabel(value: string): string {
  return !value.trim() || /[@/\\:.%~\x00-\x1f\x7f]|\b(?:[0-9a-f]{2}-){5}[0-9a-f]{2}\b/i.test(value)
    ? 'Render node' : value.slice(0, 120);
}

function nodeFrom(payload: unknown): RenderNode {
  const row = responseRecord(payload);
  const state = oneOf(row.state, ['online', 'busy', 'draining', 'asleep_wakeable', 'offline', 'quarantined']);
  if (!state) return malformed();
  const power = row.power == null ? {} : responseRecord(row, 'power');
  const watchdog: Record<string, WatchdogState> = Object.create(null);
  if (row.watchdog != null) {
    for (const [label, value] of Object.entries(responseRecord(row, 'watchdog'))) {
      const status = oneOf(value, ['ok', 'backoff', 'quarantined']);
      if (status) watchdog[label] = status;
    }
  }
  const total = measurement(row.vram_total_mib, true);
  const free = measurement(row.vram_free_mib, true);
  return {
    device_id: identifier(row.device_id),
    label: safeRenderNodeLabel(typeof row.label === 'string' ? row.label : ''),
    state,
    vram_total_mib: total,
    vram_free_mib: total !== null && free !== null && free > total ? null : free,
    queue_depth: measurement(row.queue_depth, true),
    in_flight: measurement(row.in_flight, true),
    processing_mode: oneOf(row.processing_mode, ['off', 'headless_gpu_box', 'laptop_light']),
    display_class: oneOf(row.display_class, ['headless_capable', 'interactive']),
    service_mode: oneOf(row.service_mode, ['unattended', 'attended', 'off']),
    power: {
      limit_w: measurement(power.limit_w), default_limit_w: measurement(power.default_limit_w),
      draw_w: measurement(power.draw_w), temp_c: measurement(power.temp_c),
      cap_required: boolean(power.cap_required), cap_satisfied: boolean(power.cap_satisfied),
    },
    watchdog,
    wake_via: row.wake_via == null ? [] : responseArray(row, 'wake_via').map(identifier),
  };
}

export async function fetchRenderNodes(): Promise<RenderNodeFleet> {
  const payload = await requestJson(root, { method: 'GET' });
  const fleet = Array.isArray(payload) ? { nodes: payload } : responseRecord(payload);
  const nodes = responseArray(fleet, 'nodes').map(nodeFrom);
  if (new Set(nodes.map((node) => node.device_id)).size !== nodes.length) return malformed();
  return {
    nodes,
    viewer_role: oneOf(fleet.viewer_role, ['owner', 'member', 'admin', 'editor', 'viewer', 'service']),
  };
}

/** An HTTP request id, never a second node identity (RN-1). */
export function newRenderNodeRequestId(): string {
  return globalThis.crypto.randomUUID();
}

async function post(path: string, body: Record<string, unknown>, request: RenderNodeRequest): Promise<void> {
  const requestId = request.idempotencyKey;
  if (typeof requestId !== 'string' || !/^[A-Za-z0-9_-]{1,128}$/.test(requestId)) {
    throw new ApiClientError('malformed', 0, 'invalid_request', 'A request id is required.');
  }
  responseRecord(await requestJson(path, {
    method: 'POST',
    headers: { 'Idempotency-Key': requestId },
    body: JSON.stringify(body),
  }));
}

function workspaceControlRoot(workspaceId: string, deviceId: string): string {
  if (typeof workspaceId !== 'string' || !/^wsp_[0-9a-f]{32}$/.test(workspaceId)) return malformed();
  return `/v1/workspaces/${id(workspaceId)}/render-nodes/${id(identifier(deviceId))}`;
}
export async function wakeRenderNode(deviceId: string, workspaceId: string, request: RenderNodeRequest): Promise<void> {
  await post(`${workspaceControlRoot(workspaceId, deviceId)}/wake`, { approve: true }, request);
}
export async function setRenderNodePower(deviceId: string, workspaceId: string, action: PowerAction, request: RenderNodeRequest): Promise<void> {
  if (!oneOf(action, ['sleep', 'shutdown', 'restart'])) return malformed();
  await post(`${workspaceControlRoot(workspaceId, deviceId)}/power/${id(action)}`, { approve: true }, request);
}
export async function resetRenderNodeWatchdog(deviceId: string, workspaceId: string, runtime: string, request: RenderNodeRequest): Promise<void> {
  if (typeof runtime !== 'string' || !/^[A-Za-z0-9_.-]{1,100}$/.test(runtime)) return malformed();
  await post(`${workspaceControlRoot(workspaceId, deviceId)}/watchdog/reset`, { approve: true, runtime }, request);
}
export async function setRenderNodeProcessingMode(
  deviceId: string,
  workspaceId: string,
  action: 'enter' | 'restore' | 'status',
  request: RenderNodeRequest,
  profile: 'headless_gpu_box' | 'laptop_light',
): Promise<void> {
  if (!oneOf(action, ['enter', 'restore', 'status'])) return malformed();
  // Empty arguments use the device's own locally configured profile.
  await post(`${workspaceControlRoot(workspaceId, deviceId)}/processing-mode/${id(action)}`, {
    approve: true, ...(action === 'enter' ? { profile } : {}),
  }, request);
}

/** RN-17: never reflect an exception message, arbitrary code or response detail. */
export function renderNodeFailureMessage(failure: unknown): string {
  if (failure instanceof ApiClientError) {
    const messages: Record<string, string> = {
      render_nodes_not_entitled: 'Render nodes are not included in your plan.',
      owner_required: 'Only an organization owner can control render nodes.',
      device_not_found: 'This render node is no longer available.',
      workspace_not_found: 'Choose an active workspace to queue this node request.',
      no_waker_available: 'No awake waker is available for this node.',
      power_busy: 'This node is busy. Wait for its work to finish.',
      device_busy: 'This node is busy. Wait for its work to finish.',
      power_unsupported_platform: 'Power controls are unavailable on this platform.',
      power_cooldown: 'A power action was recently requested. Wait before trying again.',
      node_quarantined: 'A runtime is quarantined. Review the watchdog status.',
    };
    if (Object.prototype.hasOwnProperty.call(messages, failure.code)) return messages[failure.code];
  }
  return 'The request could not be completed. Refresh node status before trying again.';
}
